import { FC, useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { MdAdd, MdEdit } from 'react-icons/md'
import { useToast } from '../../../Hooks/ToasContext'
import { HCPFlowEnum, IHCPFlowStatus, IHCPFlowStatusInput } from '../../../Services/HCP/flows/hcp-flow.interface'
import { ManageHCPFlowStatusUseCase } from '../../../Usecase/HCP/Flows/manage-hcp-flow-status-usecase'

interface IFlowManagementStatusDialogProps {
  open: boolean
  flow: HCPFlowEnum
  manageHCPFlowStatusUseCase: ManageHCPFlowStatusUseCase
  onClose: () => void
  onSaved: () => void
}

type DialogMode = 'list' | 'create' | 'edit'

const FLOW_LABELS: Partial<Record<HCPFlowEnum, string>> = {
  [HCPFlowEnum.SHEET]: 'Montagem de Cartelas',
  [HCPFlowEnum.PRODUCTION]: 'Produção',
  [HCPFlowEnum.MACHINE_PRODUCTION]: 'Produção de Máquinas',
  [HCPFlowEnum.LOGISTICS]: 'Logística',
}

function getErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return 'Erro ao salvar coluna.'
  const record = error as Record<string, unknown>
  return String(record.error ?? record.message ?? 'Erro ao salvar coluna.')
}

const FlowManagementStatusDialog: FC<IFlowManagementStatusDialogProps> = ({
  open,
  flow,
  manageHCPFlowStatusUseCase,
  onClose,
  onSaved,
}) => {
  const { showToast } = useToast()
  const [mode, setMode] = useState<DialogMode>('list')
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [statuses, setStatuses] = useState<IHCPFlowStatus[]>([])
  const [editing, setEditing] = useState<IHCPFlowStatus | null>(null)
  const [name, setName] = useState('')
  const [sequence, setSequence] = useState('')
  const [masterCode, setMasterCode] = useState('')
  const [familyValue, setFamilyValue] = useState('')
  const [volumetryValue, setVolumetryValue] = useState('')
  const [isActive, setIsActive] = useState(true)
  const isSheetFlow = flow === HCPFlowEnum.SHEET

  const resetForm = () => {
    setName('')
    setSequence('')
    setMasterCode('')
    setFamilyValue('')
    setVolumetryValue('')
    setIsActive(true)
    setEditing(null)
  }

  const loadStatuses = useCallback(async () => {
    setLoading(true)
    try {
      const result = await manageHCPFlowStatusUseCase.listStatuses(flow)
      if (result.isFailure) {
        showToast(getErrorMessage(result.error), 'error')
        return
      }
      const value = result.getValue() ?? []
      const items = [...value].sort((a, b) => a.sequence - b.sequence)
      setStatuses(items)
    } finally {
      setLoading(false)
    }
  }, [flow, manageHCPFlowStatusUseCase, showToast])

  useEffect(() => {
    if (!open) return
    setMode('list')
    resetForm()
    loadStatuses()
  }, [open, flow, loadStatuses])

  const openCreate = () => {
    resetForm()
    setMode('create')
  }

  const openEdit = (status: IHCPFlowStatus) => {
    setEditing(status)
    setName(status.name)
    setSequence(String(status.sequence))
    setMasterCode(status.masterCode ?? '')
    setFamilyValue(status.sheetRule?.familyValue ?? '')
    setVolumetryValue(status.sheetRule?.volumetryValue ?? '')
    setIsActive(status.isActive)
    setMode('edit')
  }

  const handleSave = async () => {
    const trimmedName = name.trim()
    if (!trimmedName) {
      showToast('Informe o nome da coluna.', 'warning')
      return
    }

    const parsedSequence = sequence.trim() ? Number(sequence) : undefined
    if (parsedSequence !== undefined && (!Number.isFinite(parsedSequence) || parsedSequence <= 0)) {
      showToast('Sequência inválida.', 'warning')
      return
    }

    const trimmedMasterCode = masterCode.trim()
    const trimmedFamilyValue = familyValue.trim()
    const trimmedVolumetryValue = volumetryValue.trim()

    if (isSheetFlow && mode === 'create') {
      if (parsedSequence === undefined) {
        showToast('Informe a sequencia da coluna.', 'warning')
        return
      }

      if (!trimmedMasterCode) {
        showToast('Informe o codigo mestre da coluna.', 'warning')
        return
      }

      if (!trimmedFamilyValue) {
        showToast('Informe a familia da coluna.', 'warning')
        return
      }

      if (!trimmedVolumetryValue) {
        showToast('Informe a volumetria da coluna.', 'warning')
        return
      }
    }

    setSaving(true)
    try {
      const payload: IHCPFlowStatusInput = {
        name: trimmedName,
        ...(parsedSequence !== undefined ? { sequence: parsedSequence } : {}),
        ...(mode === 'edit' ? { isActive } : {}),
        ...(trimmedMasterCode ? { masterCode: trimmedMasterCode } : {}),
        ...(isSheetFlow && trimmedFamilyValue ? { familyValue: trimmedFamilyValue } : {}),
        ...(isSheetFlow && trimmedVolumetryValue ? { volumetryValue: trimmedVolumetryValue } : {}),
      }

      const result = mode === 'edit' && editing
        ? await manageHCPFlowStatusUseCase.update(flow, editing.id, payload)
        : await manageHCPFlowStatusUseCase.create(flow, payload)

      if (result.isFailure) {
        showToast(getErrorMessage(result.error), 'error')
        return
      }

      showToast(mode === 'edit' ? 'Coluna atualizada.' : 'Coluna criada.', 'success')
      setMode('list')
      resetForm()
      await loadStatuses()
      onSaved()
    } finally {
      setSaving(false)
    }
  }

  const flowLabel = FLOW_LABELS[flow] ?? flow

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Colunas do fluxo — {flowLabel}
      </DialogTitle>

      <DialogContent dividers>
        {mode === 'list' && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Cadastre as etapas exibidas como colunas no Kanban.
              </Typography>
              <Button
                size="small"
                variant="contained"
                startIcon={<MdAdd />}
                onClick={openCreate}
              >
                Nova coluna
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Seq.</TableCell>
                    <TableCell>Código</TableCell>
                    <TableCell>Nome</TableCell>
                    {isSheetFlow && <TableCell>Código mestre</TableCell>}
                    {isSheetFlow && <TableCell>Família</TableCell>}
                    {isSheetFlow && <TableCell>Volumetria</TableCell>}
                    <TableCell>Ativa</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statuses.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isSheetFlow ? 8 : 5}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          Nenhuma coluna cadastrada. Crie a primeira coluna do fluxo.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {statuses.map((status) => (
                    <TableRow key={status.id} hover>
                      <TableCell>{status.sequence}</TableCell>
                      <TableCell>
                        <Chip label={status.displayCode} size="small" sx={{ fontWeight: 600 }} />
                      </TableCell>
                      <TableCell>{status.name}</TableCell>
                      {isSheetFlow && (
                        <TableCell>{status.masterCode || '-'}</TableCell>
                      )}
                      {isSheetFlow && (
                        <TableCell>{status.sheetRule?.familyValue || '-'}</TableCell>
                      )}
                      {isSheetFlow && (
                        <TableCell>{status.sheetRule?.volumetryValue || '-'}</TableCell>
                      )}
                      <TableCell>
                        <Chip
                          label={status.isActive ? 'Sim' : 'Não'}
                          size="small"
                          color={status.isActive ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar coluna">
                          <IconButton size="small" onClick={() => openEdit(status)}>
                            <MdEdit size={18} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}

        {(mode === 'create' || mode === 'edit') && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {mode === 'create' ? 'Nova coluna' : `Editar — ${editing?.displayCode ?? ''}`}
            </Typography>

            <TextField
              label="Nome da coluna"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              placeholder="Ex.: Em produção"
            />

            <TextField
              label="Sequência (opcional)"
              value={sequence}
              onChange={(e) => setSequence(e.target.value)}
              fullWidth
              size="small"
              type="number"
              helperText="Se vazio, o backend usa a próxima sequência disponível."
            />

            {isSheetFlow && (
              <>
                <TextField
                  label="Código mestre"
                  value={masterCode}
                  onChange={(e) => setMasterCode(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Ex.: 124"
                />

                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                  <TextField
                    label="Família"
                    value={familyValue}
                    onChange={(e) => setFamilyValue(e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="Ex.: PPT"
                  />

                  <TextField
                    label="Volumetria"
                    value={volumetryValue}
                    onChange={(e) => setVolumetryValue(e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="Ex.: 0210"
                  />
                </Box>
              </>
            )}

            {mode === 'edit' && (
              <FormControlLabel
                control={(
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                )}
                label="Coluna ativa"
              />
            )}

            <Divider />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="text"
                onClick={() => {
                  setMode('list')
                  resetForm()
                }}
              >
                Voltar
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Fechar
        </Button>
        {(mode === 'create' || mode === 'edit') && (
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default FlowManagementStatusDialog
